import React from "react";

export const TYPEAHEAD_WINDOW_MS = 500;

const TYPEAHEAD_KEY = /^[a-z0-9]$/i;

let buffer = "";
let lastTime = 0;
let lastElement = null;

export function optionLabel(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(optionLabel).join("");
  if (React.isValidElement(node) || node?.props) {
    return optionLabel(node.props?.children);
  }
  return "";
}

function isMenuItem(child) {
  if (!React.isValidElement(child)) return false;
  if (child.type?.muiName === "MenuItem") return true;
  return typeof child.type !== "string" && child.props && "value" in child.props;
}

export function collectSelectOptions(children) {
  const options = [];
  React.Children.forEach(children, (child) => {
    if (!isMenuItem(child)) return;
    if (child.props.disabled) return;
    const { value } = child.props;
    if (value == null || value === "") return;
    const label = optionLabel(child.props.children).trim();
    if (!label) return;
    options.push({ value, label, element: child });
  });
  return options;
}

function labelMatches(label, query) {
  const text = label.trim().toLowerCase();
  if (text.startsWith(query)) return true;
  if (query.length < 2) return false;
  const codes = text.match(/\(([^)]+)\)/g) || [];
  return codes.some((wrapped) => wrapped.slice(1, -1).trim().startsWith(query));
}

export function findTypeaheadMatch(options, search, startIndex) {
  const query = String(search || "").toLowerCase();
  if (!query || options.length === 0) return null;
  const count = options.length;
  const start = ((startIndex % count) + count) % count;
  for (let i = 0; i < count; i += 1) {
    const option = options[(start + i) % count];
    if (labelMatches(option.label, query)) return option;
  }
  return null;
}

export function resolveTypeahead({
  buffer: currentBuffer = "",
  lastTime: previousTime = 0,
  now,
  key,
  options,
  currentValue,
}) {
  const letter = String(key || "").toLowerCase();
  const expired =
    !currentBuffer || now - previousTime > TYPEAHEAD_WINDOW_MS;
  const repeating =
    !expired &&
    currentBuffer.length > 0 &&
    [...currentBuffer].every((char) => char === letter);

  let nextBuffer;
  let startIndex;
  if (repeating) {
    nextBuffer = letter;
    const currentIndex = options.findIndex(
      (option) => String(option.value) === String(currentValue)
    );
    startIndex = currentIndex + 1;
  } else {
    nextBuffer = expired ? letter : `${currentBuffer}${letter}`;
    startIndex = 0;
  }

  const match = findTypeaheadMatch(options, nextBuffer, startIndex);
  if (!match) {
    return {
      buffer: repeating || expired ? "" : currentBuffer,
      lastTime: now,
      match: null,
    };
  }

  return { buffer: nextBuffer, lastTime: now, match };
}

function fiberKey(element) {
  return Object.keys(element).find((key) => key.startsWith("__reactFiber$"));
}

function findSelectInputProps(element) {
  const key = fiberKey(element);
  if (!key) return null;
  let fiber = element[key];
  for (let depth = 0; depth < 25 && fiber; depth += 1) {
    const props = fiber.memoizedProps;
    if (
      props &&
      typeof props.onChange === "function" &&
      "multiple" in props &&
      collectSelectOptions(props.children).length > 0
    ) {
      return props;
    }
    fiber = fiber.return;
  }
  return null;
}

function onKeyDown(event) {
  if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
  if (!TYPEAHEAD_KEY.test(event.key || "")) return;

  const target = event.target;
  if (!target || typeof target.closest !== "function") return;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
  if (target.isContentEditable) return;

  const display = target.closest(".MuiSelect-select");
  if (!display) return;
  if (display.getAttribute("aria-expanded") === "true") return;
  if (display.getAttribute("aria-disabled") === "true") return;

  const props = findSelectInputProps(display);
  if (!props || props.multiple || props.disabled || props.readOnly) return;

  const options = collectSelectOptions(props.children);
  if (options.length === 0) return;

  const now = performance.now();
  if (display !== lastElement) buffer = "";
  const resolved = resolveTypeahead({
    buffer,
    lastTime,
    now,
    key: event.key,
    options,
    currentValue: props.value,
  });
  buffer = resolved.buffer;
  lastTime = resolved.lastTime;
  lastElement = display;

  if (!resolved.match) return;
  if (String(resolved.match.value) === String(props.value)) return;

  event.preventDefault();
  props.onChange(
    {
      target: { name: props.name ?? "", value: resolved.match.value },
      preventDefault() {},
      stopPropagation() {},
    },
    resolved.match.element
  );
}

export function resetSelectTypeahead() {
  buffer = "";
  lastTime = 0;
  lastElement = null;
}

export function installSelectTypeahead() {
  resetSelectTypeahead();
  document.addEventListener("keydown", onKeyDown, true);
  return () => {
    document.removeEventListener("keydown", onKeyDown, true);
    resetSelectTypeahead();
  };
}
